import { 
  Divider, 
  EmptyPlaceholder,
  SearchInput, 
  Tooltip 
} from "@components"

import { mySqlToLocal } from "@helpers"
import { Button, Checkbox } from "@heroui/react"

import { 
  CalendarDaysIcon, 
  CheckCircleIcon, 
  PaperAirplaneIcon, 
  XCircleIcon 
} from "@heroicons/react/24/solid"

import InfiniteScroll from 'react-infinite-scroll-component';
import PropTypes from 'prop-types'

const GuardianSelector = ({ 
  handleClearAll,
  handleLoadMore,
  handleSearchChange, 
  handleSearchClear,
  handleSelectAll,
  handleToggleSelected,
  invitations,
  noOfDeselected,
  noOfSelected,
  searchTerm,
  selectedInvitationIds = [],
}) => (
  <div>
    <div className="flex items-center gap-2">
      <SearchInput
        debounceMilliSeconds={300}
        placeholder="Search by Guardian / Student"
        handleChange={handleSearchChange}
        handleClear={handleSearchClear}
        value={searchTerm}
      />

      <Button className="shrink-0" onPress={() => handleSelectAll()}>Select All ({noOfDeselected})</Button>
      <Button className="shrink-0" onPress={() => handleClearAll()}>Clear All ({noOfSelected})</Button>
    </div>

    {invitations.length === 0 && (
      <EmptyPlaceholder 
        classNames="mt-3" 
        title="No guardians found" 
        description="There are no guardians available. Adjust your search or the filters set on the previous step." 
      />
    )}

    {invitations.length > 0 && (
      <div id="invitation-selector" className="overflow-y-scroll max-h-[45vh] mt-4">
        <InfiniteScroll dataLength={invitations.length} next={() => handleLoadMore()} hasMore={true} scrollableTarget="invitation-selector">
          {invitations.map((invitation) => {
            return (
              <div key={invitation.id}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Checkbox 
                      className="mr-2" 
                      size="lg" 
                      isSelected={selectedInvitationIds.includes(invitation.id)} 
                      onValueChange={(e) => handleToggleSelected(invitation.id, e)} 
                    />
                    
                    <div>
                      <h2 className="font-semibold">{invitation.guardian}</h2>
                      <p className="text-content2-foreground">{invitation.students}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mx-3">
                    {invitation.activated_at && (
                      <Tooltip content={`Account activated on ${mySqlToLocal(invitation.activated_at).format("D MMM YY HH:mm")}`} placement="top">
                        <CheckCircleIcon className="w-10 h-10 text-green-500" />
                      </Tooltip>
                    )}

                    {!invitation.activated_at && (
                      <Tooltip content="Account has not been activated" placement="top">
                        <XCircleIcon className="w-10 h-10 text-gray-400" />
                      </Tooltip>
                    )}

                    <Tooltip content={`${invitation.booking_counts} / ${invitation.slot_counts} Bookings made`} placement="top">
                      <CalendarDaysIcon className={`w-10 h-10 ${invitation.booking_counts ? 'text-green-500' : 'text-gray-400'}`} /> 
                    </Tooltip>

                    {invitation.last_sent_at && (
                      <Tooltip content={`Last invitation email sent on ${mySqlToLocal(invitation.last_sent_at).format("D MMM YY HH:mm")}`} placement="top">
                        <PaperAirplaneIcon className="w-10 h-10 text-green-500" />
                      </Tooltip>
                    )}

                    {!invitation.last_sent_at && (
                      <Tooltip content={`An invitation email has never been sent.`} placement="top">
                        <PaperAirplaneIcon className="w-10 h-10 text-gray-400" />
                      </Tooltip>
                    )}
                  </div>
                </div>
                
                <Divider className="my-3" />
              </div>
            )
          })}
        </InfiniteScroll>
      </div>
    )}
  </div>
)

GuardianSelector.propTypes = {
  handleClearAll: PropTypes.func.isRequired,
  handleLoadMore: PropTypes.func.isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  handleSearchClear: PropTypes.func.isRequired,
  handleSelectAll: PropTypes.func.isRequired,
  handleToggleSelected: PropTypes.func.isRequired,
  invitations: PropTypes.arrayOf(PropTypes.object).isRequired,
  noOfDeselected: PropTypes.number.isRequired,
  noOfSelected: PropTypes.number.isRequired,
  searchTerm: PropTypes.string.isRequired,
  selectedInvitationIds: PropTypes.array,
}

export default GuardianSelector